package ai.wagemix.web.theme

import com.fsryan.foundation.web.theme.FSBreakpoint
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.textTransform
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px

/*
 * The Wagemix type scale (Penpot `wagemix.type.*`): Inter, sizes in rem so text follows the
 * reader's browser setting, line heights as ratios. The display and section-title styles are
 * responsive: the compact value is the base and the full value applies from the expanded
 * window size class up, which is how the Desktop/Mobile boards differ.
 */

private const val ROOT_FONT_SIZE_PX = 16.0

private fun type(size: Double, lineHeight: Double, weight: FontWeight, letterSpacing: Double = 0.0): Modifier {
    return Modifier
        .fontSize((size / ROOT_FONT_SIZE_PX).cssRem)
        .lineHeight(lineHeight)
        .fontWeight(weight)
        .letterSpacing(letterSpacing.px)
}

/** Figures line up in columns and stat tiles. */
private fun Modifier.tabularNumbers(): Modifier = styleModifier { property("font-variant-numeric", "tabular-nums") }

/** Landing hero headline: 36/1.15 compact, 56/1.1 from expanded. */
val WMDisplayTextStyle = CssStyle {
    base { type(size = 36.0, lineHeight = 1.15, weight = FontWeight.Bold, letterSpacing = -1.0) }
    FSBreakpoint.Expanded { type(size = 56.0, lineHeight = 1.1, weight = FontWeight.Bold, letterSpacing = -1.5) }
}

/** App page titles and auth titles. */
val WMPageTitleTextStyle = CssStyle.base { type(size = 28.0, lineHeight = 1.25, weight = FontWeight.Bold, letterSpacing = -0.5) }

/** Marketing section titles: 26/1.25 compact, 36/1.2 from expanded. */
val WMSectionTitleTextStyle = CssStyle {
    base { type(size = 26.0, lineHeight = 1.25, weight = FontWeight.Bold, letterSpacing = -0.5) }
    FSBreakpoint.Expanded { type(size = 36.0, lineHeight = 1.2, weight = FontWeight.Bold, letterSpacing = -0.75) }
}

/** Card and panel titles. */
val WMCardTitleTextStyle = CssStyle.base { type(size = 18.0, lineHeight = 1.4, weight = FontWeight.SemiBold) }

/** Hero and section leads. */
val WMLeadTextStyle = CssStyle.base { type(size = 18.0, lineHeight = 1.6, weight = FontWeight.Normal) }

val WMBodyTextStyle = CssStyle.base { type(size = 16.0, lineHeight = 1.5, weight = FontWeight.Normal) }
val WMBodyStrongTextStyle = CssStyle.base { type(size = 16.0, lineHeight = 1.5, weight = FontWeight.SemiBold) }

/** Helper text and table cells. */
val WMBodySmallTextStyle = CssStyle.base { type(size = 14.0, lineHeight = 1.5, weight = FontWeight.Normal) }

/** Form labels, nav items, tabs. */
val WMLabelTextStyle = CssStyle.base { type(size = 14.0, lineHeight = 1.4, weight = FontWeight.Medium) }

/** Buttons and active tabs. */
val WMLabelStrongTextStyle = CssStyle.base { type(size = 14.0, lineHeight = 1.4, weight = FontWeight.SemiBold) }

/** Meta text and legends. */
val WMCaptionTextStyle = CssStyle.base { type(size = 12.0, lineHeight = 1.4, weight = FontWeight.Medium) }

/** Eyebrows and stat labels. */
val WMOverlineTextStyle = CssStyle.base {
    type(size = 12.0, lineHeight = 1.3, weight = FontWeight.SemiBold, letterSpacing = 0.8)
        .textTransform(TextTransform.Uppercase)
}

/** Stat tile values. */
val WMStatTextStyle = CssStyle.base {
    type(size = 28.0, lineHeight = 1.2, weight = FontWeight.Bold, letterSpacing = -0.5).tabularNumbers()
}

/** Compact stat tile values. */
val WMStatSmallTextStyle = CssStyle.base {
    type(size = 20.0, lineHeight = 1.2, weight = FontWeight.Bold).tabularNumbers()
}

/** The "Wagemix" wordmark beside the logo mark. */
val WMWordmarkTextStyle = CssStyle.base { type(size = 20.0, lineHeight = 1.2, weight = FontWeight.Bold, letterSpacing = -0.5) }

/** Numbers that must line up (percentages in occupation rows, table figures). */
val WMTabularTextStyle = CssStyle.base { Modifier.tabularNumbers() }
